import type { CmsCompanyInfo, CmsFounder, CmsPortfolioInput, CmsSocialLink, CmsStatItem } from "../types";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\d\s.-]{6,20}$/;
const URL_RE = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i;

export function validateEmail(value: string, field = "Email"): string | null {
  if (!value.trim()) return `${field} is required`;
  if (!EMAIL_RE.test(value.trim())) return `${field} must be a valid email`;
  return null;
}

export function validateOptionalEmail(value: string, field = "Email"): string | null {
  if (!value.trim()) return null;
  return validateEmail(value, field);
}

export function validatePhone(value: string, field = "Phone"): string | null {
  if (!value.trim()) return null;
  if (!PHONE_RE.test(value.trim())) return `${field} must be a valid phone number`;
  return null;
}

export function validateUrl(value: string, field = "URL", required = false): string | null {
  if (!value.trim()) return required ? `${field} is required` : null;
  if (!URL_RE.test(value.trim())) return `${field} must be a valid URL`;
  return null;
}

export function validateRequired(value: string, field: string): string | null {
  if (!value.trim()) return `${field} is required`;
  return null;
}

export function validateStats(stats: CmsStatItem[]): string | null {
  for (const stat of stats) {
    const titleError = validateRequired(stat.title, "Stat title");
    if (titleError) return titleError;
    const valueError = validateRequired(stat.value, "Stat value");
    if (valueError) return valueError;
  }
  return null;
}

export function validateCompany(company: CmsCompanyInfo): string | null {
  return (
    validateOptionalEmail(company.primaryEmail, "Primary email") ??
    validateOptionalEmail(company.supportEmail, "Support email") ??
    validatePhone(company.primaryPhone, "Primary phone") ??
    validatePhone(company.secondaryPhone, "Secondary phone") ??
    validateUrl(company.googleMapsLink, "Google Maps link") ??
    validateUrl(company.websiteUrl ?? "", "Website URL")
  );
}

export function validateFounder(founder: CmsFounder): string | null {
  return (
    validateRequired(founder.fullName, "Full name") ??
    validateOptionalEmail(founder.email ?? "", "Email") ??
    validatePhone(founder.phone ?? "", "Phone")
  );
}

export function validatePortfolioInput(input: Partial<CmsPortfolioInput>, isCreate = false): string | null {
  if (isCreate) {
    const nameError = validateRequired(input.name ?? "", "Project name");
    if (nameError) return nameError;
  }
  if (input.projectLink) {
    return validateUrl(input.projectLink, "Project link");
  }
  return null;
}

export function validateSocialLinks(links: CmsSocialLink[]): string | null {
  for (const link of links) {
    if (!link.platform) return "Platform is required";
    if (link.profileUrl) {
      const url = link.profileUrl.trim();
      if (link.platform === "email" || url.toLowerCase().startsWith("mailto:")) {
        const email = url.toLowerCase().startsWith("mailto:") ? url.slice(7) : url;
        const emailError = validateEmail(email, "Email");
        if (emailError) return emailError;
        continue;
      }
      const urlError = validateUrl(link.profileUrl, "Profile URL");
      if (urlError) return urlError;
    }
  }
  return null;
}

export function validateImageFile(file: File, maxBytes = 5 * 1024 * 1024): string | null {
  if (!file.type.startsWith("image/")) return "Only image files are allowed";
  if (file.size > maxBytes) return "Image must be 5MB or smaller";
  return null;
}

export function validateDocumentFile(file: File, maxBytes = 5 * 1024 * 1024): string | null {
  const allowed = ["application/pdf", "image/png", "image/jpeg", "image/webp", "image/svg+xml"];
  if (!allowed.includes(file.type)) return "Invalid file type";
  if (file.size > maxBytes) return "File must be 5MB or smaller";
  return null;
}
